import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { Drawer, QueryState, RecordTable, StatusPill, formatDate, formatDateTime } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import { signedUrlsFor } from '@/api/documents';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { esc } from '@/lib/consentDocument';
import { htmlToPdf } from '@/lib/pdf';
import { landUseSummary, type LandUseEvent, type LandUsePoint, type LandUseSummary } from '@/lib/landUse';
import { toast } from '@/lib/toast';

interface BatchRow {
  id: string; batch_code: string | null; batch_type: string; window_from: string | null; window_to: string | null; note: string | null;
  requested_at: string | null; requested_by_name: string | null; plot_count: number | null; job_count: number | null; state: string;
  provider_key: string | null;
}

export interface ResultRow {
  id: string; batch_id: string; batch_code: string | null; batch_type: string; window_from: string | null; window_to: string | null;
  note: string | null; requested_at: string | null; requested_by_name: string | null; plot_id: string; plot_code: string | null;
  farmer_name: string | null; item_state: string; skip_reason: string | null; job_state: string; completed_at: string | null;
  error_message: string | null; usable_scenes: number | null; other_scenes: number | null; first_ndvi: number | null; last_ndvi: number | null;
  first_date: string | null; last_date: string | null; flagged: number | null; open_flags: number | null;
  before_document_id: string | null; after_document_id: string | null; before_date: string | null; before_ndvi: number | null;
  after_date: string | null; after_ndvi: number | null; comparison_note: string | null;
  mapped_area_ha: number | null; declared_area_ha: number | null;
  season_document_id: string | null; season_year: number | null; season_date: string | null; season_ndvi: number | null;
  /** [minLng, minLat, maxLng, maxLat] the pictures show; null for pictures of the plot alone (before 800000). */
  render_bbox: [number, number, number, number] | null;
  /** The boundary the check measured (840000). */
  monitoring_id: string | null;
  /** The report PDF the worker wrote when the check completed (2026-10-05). */
  report_document_id: string | null;
}

/** What api.fn_request_land_use_evidence returns for one plot of a request. */
interface LandUseEvidence {
  batch_item_id: string; window_from: string; window_to: string; history_from: string | null;
  observations: LandUsePoint[]; events: LandUseEvent[];
}

type Ring = Array<[number, number]>;

interface SeriesRow {
  id: string; batch_item_id: string; batch_id: string; plot_id: string; target_date: string; document_id: string | null;
  mosaic: boolean; note: string | null; acquisition_date: string | null; ndvi_mean: number | null; cloud_pct: number | null;
}

const MONTH = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** NDVI month by month as a small line (SVG markup), so a fall or a green-up reads at a glance. */
function ndviLine(points: SeriesRow[]): string {
  const known = points.filter((row) => row.ndvi_mean != null);
  if (known.length < 2) return '';
  const w = 640; const h = 120; const pad = 26;
  const x = (i: number) => pad + (i * (w - 2 * pad)) / Math.max(points.length - 1, 1);
  const y = (v: number) => h - pad - Math.max(0, Math.min(1, v)) * (h - 2 * pad);
  const path = points.map((row, i) => (row.ndvi_mean == null ? null : `${x(i).toFixed(1)},${y(Number(row.ndvi_mean)).toFixed(1)}`)).filter(Boolean).join(' ');
  const labels = points.map((row, i) => `<text x="${x(i).toFixed(1)}" y="${h - 6}" font-size="10" text-anchor="middle" fill="#555">${MONTH(row.target_date).slice(0, 3)}</text>`).join('');
  const dots = points.map((row, i) => (row.ndvi_mean == null ? '' : `<circle cx="${x(i).toFixed(1)}" cy="${y(Number(row.ndvi_mean)).toFixed(1)}" r="3.5" fill="#15803d"/><text x="${x(i).toFixed(1)}" y="${(y(Number(row.ndvi_mean)) - 7).toFixed(1)}" font-size="9.5" text-anchor="middle" fill="#14532d">${Number(row.ndvi_mean).toFixed(2)}</text>`)).join('');
  const grid = [0, 0.25, 0.5, 0.75, 1].map((v) => `<line x1="${pad}" x2="${w - pad}" y1="${y(v)}" y2="${y(v)}" stroke="#e5e7eb"/><text x="4" y="${y(v) + 3}" font-size="9" fill="#888">${v}</text>`).join('');
  return `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto">${grid}<polyline points="${path}" fill="none" stroke="#15803d" stroke-width="2"/>${dots}${labels}</svg>`;
}

/** The plot's boundary, outer ring, as [lng, lat]. */
async function boundaryOf(plotId: string): Promise<Ring | null> {
  try {
    const result = await callRpc<{ geometry?: { type: string; coordinates: unknown } } | null>('fn_plot_geojson', { p_plot_id: plotId });
    const geometry = result?.geometry;
    if (geometry?.type === 'Polygon') return (geometry.coordinates as Ring[])[0] ?? null;
    if (geometry?.type === 'MultiPolygon') return (geometry.coordinates as Ring[][])[0]?.[0] ?? null;
    return null;
  } catch {
    return null;
  }
}

/** The boundary as an SVG polygon over a picture of `bbox`, in a 1000 x 1000 frame. */
function outline(ring: Ring | null | undefined, bbox: ResultRow['render_bbox']): string {
  if (!ring?.length || !bbox) return '';
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const points = ring.map(([lng, lat]) => `${(((lng - minLng) / (maxLng - minLng)) * 1000).toFixed(1)},${(((maxLat - lat) / (maxLat - minLat)) * 1000).toFixed(1)}`).join(' ');
  return `<svg viewBox="0 0 1000 1000" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}" fill="none" stroke="#facc15" stroke-width="6" stroke-linejoin="round"/></svg>`;
}

/** "300 m across": what the picture covers, from its box. */
function across(bbox: ResultRow['render_bbox']): string | null {
  if (!bbox) return null;
  const metres = (bbox[3] - bbox[1]) * 111320;
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km across` : `${Math.round(metres / 10) * 10} m across`;
}
const hectares = (row: ResultRow) => [row.mapped_area_ha != null ? `${Number(row.mapped_area_ha).toFixed(2)} ha mapped` : null,
  row.declared_area_ha != null ? `${Number(row.declared_area_ha).toFixed(2)} ha declared` : null].filter(Boolean).join(' · ') || '--';

const KIND: Record<string, string> = { recheck: 'Check for change', investigation: 'Investigate an issue', backfill: 'Look back at history' };
const ndvi = (value: number | null) => (value == null ? '--' : Number(value).toFixed(2));
const changePct = (from: number | null, to: number | null) =>
  (from == null || to == null || Number(from) === 0 ? null : Math.round(((Number(to) - Number(from)) / Math.abs(Number(from))) * 100));
const days = (row: { window_from: string | null; window_to: string | null }) =>
  (row.window_from && row.window_to ? `${formatDate(row.window_from)} to ${formatDate(row.window_to)}` : 'Last 45 days');

/** What the satellite made of one plot, in words a field officer can repeat. */
export function resultSentence(row: ResultRow): string {
  if (row.skip_reason) return `Skipped: ${row.skip_reason}.`;
  if (row.job_state !== 'completed') return row.error_message ? `Not done: ${row.error_message}` : 'Waiting for the satellite (runs every 15 minutes).';
  const scenes = row.usable_scenes ?? 0;
  if (scenes === 0) return `No clear scene in these dates${row.other_scenes ? ` (${row.other_scenes} clouded)` : ''}; nothing could be judged.`;
  const change = changePct(row.first_ndvi, row.last_ndvi);
  const flagged = row.flagged ?? 0;
  return `${scenes} clear scene${scenes === 1 ? '' : 's'}${row.other_scenes ? `, ${row.other_scenes} clouded` : ''}. Vegetation (NDVI) ${ndvi(row.first_ndvi)} on `
    + `${row.first_date ? formatDate(row.first_date) : '--'} and ${ndvi(row.last_ndvi)} on ${row.last_date ? formatDate(row.last_date) : '--'}`
    + `${change == null ? '' : ` (${change > 0 ? '+' : ''}${change}%)`}. `
    + (flagged === 0 ? 'No material change flagged.' : `${flagged} change${flagged === 1 ? '' : 's'} flagged${row.open_flags ? `, ${row.open_flags} waiting for review` : ''}.`)
    + seasonSentence(row);
}

/** The season against now: the question a dry-season picture answers. */
function seasonSentence(row: ResultRow): string {
  if (row.season_ndvi == null || row.after_ndvi == null) return '';
  const change = changePct(row.season_ndvi, row.after_ndvi);
  return ` Against the dry season (Apr-May ${row.season_year ?? ''}, NDVI ${ndvi(row.season_ndvi)}), vegetation at the end is ${ndvi(row.after_ndvi)}`
    + `${change == null ? '' : ` (${change > 0 ? '+' : ''}${change}%)`}.`;
}

/** The land-use verdict for one plot of a request, once its check has finished. */
function landUseOf(row: ResultRow, evidence: LandUseEvidence[] | undefined): LandUseSummary | null {
  if (row.job_state !== 'completed') return null;
  const found = evidence?.find((item) => item.batch_item_id === row.id);
  if (!found) return null;
  return landUseSummary({
    windowFrom: found.window_from, windowTo: found.window_to, observations: found.observations, events: found.events,
    historyFrom: found.history_from,
    mappedHa: row.mapped_area_ha == null ? null : Number(row.mapped_area_ha),
    declaredHa: row.declared_area_ha == null ? null : Number(row.declared_area_ha),
  });
}

const VERDICT_CLASS: Record<LandUseSummary['verdict'], string> = { none: 'is-none', possible: 'is-possible', cannot_tell: 'is-unknown' };

function LandUseBlock({ summary }: { summary: LandUseSummary }) {
  const part = (title: string, lines: string[], muted = false) => (lines.length ? (
    <>
      <h5>{title}</h5>
      <ul className={muted ? 'is-muted' : undefined}>{lines.map((line) => <li key={line}>{line}</li>)}</ul>
    </>
  ) : null);
  return (
    <div className={`sat-landuse ${VERDICT_CLASS[summary.verdict]}`}>
      <strong>{summary.headline}</strong>
      <p>{summary.finding}</p>
      {part('What was seen', summary.seen)}
      {part('Why', summary.why)}
      {part('Changes flagged', summary.flags)}
      {part('Limits', summary.limits, true)}
    </div>
  );
}

/** The same, as the PDF's first-page section. */
function landUseHtml(summary: LandUseSummary): string {
  const part = (title: string, lines: string[]) => (lines.length ? `<h3>${esc(title)}</h3><ul>${lines.map((line) => `<li>${esc(line)}</li>`).join('')}</ul>` : '');
  return `<div class="luc ${VERDICT_CLASS[summary.verdict]}"><h2>${esc(summary.headline)}</h2><p><b>Finding:</b> ${esc(summary.finding)}</p>`
    + `${part('What was seen', summary.seen)}${part('Why', summary.why)}${part('Changes flagged', summary.flags)}${part('Limits', summary.limits)}</div>`;
}

/** A stored picture as a data URI, so the PDF maker can draw it. */
async function dataUri(url: string | undefined): Promise<string | null> {
  if (!url) return null;
  try {
    const blob = await (await fetch(url)).blob();
    return await new Promise((resolve) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.readAsDataURL(blob); });
  } catch {
    return null;
  }
}

function Shots({ row, urls, ring }: { row: ResultRow; urls: Record<string, string>; ring: Ring | null | undefined }) {
  const svg = outline(ring, row.render_bbox);
  const shot = (documentId: string | null, label: string, date: string | null, value: number | null) => (
    <figure className="sat-compare-shot">
      {documentId && urls[documentId] ? (
        <div className="sat-compare-frame">
          <img alt={`${row.plot_code ?? 'Plot'} ${label}`} src={urls[documentId]} />
          {svg && <span className="sat-compare-outline" dangerouslySetInnerHTML={{ __html: svg }} />}
        </div>
      ) : <div className="sat-compare-empty">No clear picture</div>}
      <figcaption><strong>{label}</strong> · {date ? formatDate(date) : 'clearest of the season'} · NDVI {ndvi(value)}</figcaption>
    </figure>
  );
  return (
    <>
      <div className="sat-compare is-three">
        {row.season_year != null && shot(row.season_document_id, `Apr-May ${row.season_year}`, row.season_date, row.season_ndvi)}
        {shot(row.before_document_id, 'A month before', row.before_date, row.before_ndvi)}
        {shot(row.after_document_id, 'At the end', row.after_date, row.after_ndvi)}
      </div>
      {row.render_bbox && <small className="muted">Each picture is {across(row.render_bbox)}, north up; the plot's boundary is drawn in yellow.</small>}
    </>
  );
}

/**
 * The requests and what the satellite found (owner, 2026-09-28: "single page for request and results
 * in tabs"; "a month before and a month after. how it displays ... how the images and reports come
 * on"). A request, opened, shows each plot: the scenes seen, vegetation at the start and the end,
 * what was flagged, and the plot a month before the dates and at their end, side by side - with a
 * PDF report of the same.
 */
export function RequestResults() {
  const batches = useRows<BatchRow>('satellite_batches', { orderBy: { column: 'requested_at', ascending: false } });
  const [open, setOpen] = useState<BatchRow | null>(null);
  const series = useRows<SeriesRow>('satellite_request_series', { filters: { batch_id: open?.id ?? undefined }, orderBy: { column: 'target_date' } }, Boolean(open));
  const results = useRows<ResultRow>('satellite_request_results', { filters: { batch_id: open?.id ?? undefined }, orderBy: { column: 'plot_code' } }, Boolean(open));
  const pictureIds = [...(results.data ?? []).flatMap((row) => [row.season_document_id, row.before_document_id, row.after_document_id, row.report_document_id]),
    ...(series.data ?? []).map((row) => row.document_id)].filter((id): id is string => Boolean(id));
  const seriesOf = (plotId: string) => (series.data ?? []).filter((row) => row.plot_id === plotId);
  const urls = useQuery({
    queryKey: ['sat-request-pictures', pictureIds.join(',')],
    enabled: isSupabaseConfigured && pictureIds.length > 0,
    queryFn: () => signedUrlsFor(pictureIds),
  });
  const evidence = useQuery({
    queryKey: ['sat-land-use', open?.id],
    enabled: isSupabaseConfigured && Boolean(open),
    queryFn: () => callRpc<LandUseEvidence[]>('fn_request_land_use_evidence', { p_batch_id: open!.id }),
  });
  const [busy, setBusy] = useState(false);
  const plotIds = [...new Set((results.data ?? []).map((row) => row.plot_id))];
  const rings = useQuery({
    queryKey: ['sat-request-boundaries', plotIds.join(',')],
    enabled: isSupabaseConfigured && plotIds.length > 0,
    queryFn: async () => Object.fromEntries(await Promise.all(plotIds.map(async (id) => [id, await boundaryOf(id)] as const))),
  });

  async function report() {
    if (!open) return;
    setBusy(true);
    try {
      const rows = results.data ?? [];
      const pictures: Record<string, string | null> = {};
      for (const id of pictureIds) pictures[id] = await dataUri(urls.data?.[id]);
      const img = (id: string | null, row: ResultRow) => (id && pictures[id]
        ? `<div class="frame"><img src="${pictures[id]}" alt="">${outline(rings.data?.[row.plot_id], row.render_bbox)}</div>`
        : '<div class="empty">No clear picture</div>');
      const cap = (label: string, date: string | null, value: number | null) =>
        `<div class="cap"><b>${esc(label)}</b> · ${esc(date ? formatDate(date) : 'clearest of the season')} · NDVI ${ndvi(value)}</div>`;
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Satellite check ${esc(open.batch_code ?? '')}</title><style>
*{box-sizing:border-box}body{margin:0;font:12.5px/1.45 'Noto Sans',system-ui,sans-serif;color:#111;background:#e5e7eb}
.page{width:794px;height:1123px;margin:0 auto 16px;padding:40px 52px 64px;background:#fff;position:relative;overflow:hidden}
h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;margin:16px 0 6px}.sub{color:#555;margin:0 0 10px}
table{border-collapse:collapse;width:100%}td{border-bottom:1px solid #e2e2e2;padding:4px;vertical-align:top}td:first-child{width:30%;color:#555}
.pair{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:6px 0 4px}.frame{position:relative}.frame img,.empty{display:block;width:100%;aspect-ratio:1;border:1px solid #ccc;border-radius:6px;object-fit:cover}
.frame svg{position:absolute;inset:0;width:100%;height:100%}
.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:8px}.grid .cap{font-size:10.5px}
.empty{display:flex;align-items:center;justify-content:center;color:#888;background:#f5f5f5}.cap{font-size:11px;color:#444;margin-top:2px}
.foot{position:absolute;bottom:18px;left:52px;right:52px;font-size:10px;color:#777;display:flex;justify-content:space-between}
.note{border:1px solid #bbb;border-radius:6px;padding:8px 10px;background:#fafafa;margin-top:10px}
.luc{border:1px solid #d4d4d8;border-left:4px solid #15803d;border-radius:6px;padding:4px 12px 8px;margin-top:12px}.luc.is-possible{border-left-color:#dc2626;background:#fef2f2}
.luc.is-unknown{border-left-color:#f59e0b;background:#fffbeb}.luc h2{margin:6px 0 4px}.luc h3{font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:#555;margin:8px 0 2px}
.luc ul{margin:0;padding-left:18px}.luc li{margin:1px 0}.luc p{margin:0 0 4px}</style></head><body>
${rows.map((row) => `<section class="page">
<h1>Satellite check · ${esc(row.plot_code ?? '')}</h1>
<p class="sub">${esc(row.farmer_name ?? '')} · Request ${esc(open.batch_code ?? '')} · ${esc(KIND[open.batch_type] ?? open.batch_type)}</p>
<table><tr><td>Dates checked</td><td>${esc(days(open))}</td></tr><tr><td>Asked by</td><td>${esc(open.requested_by_name ?? '--')}, ${esc(formatDateTime(open.requested_at))}</td></tr>
<tr><td>Why</td><td>${esc(open.note || '--')}</td></tr><tr><td>Source</td><td>Copernicus Sentinel-2 (10 m), ${esc(open.provider_key ?? '')}</td></tr>
<tr><td>Plot area</td><td>${esc(hectares(row))}</td></tr>
<tr><td>Checked</td><td>${esc(row.completed_at ? formatDateTime(row.completed_at) : '--')}</td></tr></table>
${(() => { const summary = landUseOf(row, evidence.data); return summary ? landUseHtml(summary) : `<h2>What the satellite saw</h2><p>${esc(resultSentence(row))}</p>`; })()}
${row.comparison_note ? `<p class="sub">${esc(row.comparison_note)}</p>` : ''}
<div class="note">Satellite observation is remote evidence. A change here is something to check against field records, never a finding on its own. Imagery: Copernicus Sentinel-2, true colour; NDVI measures green vegetation (0 bare, 1 dense).</div>
<div class="foot"><span>Request ${esc(open.batch_code ?? '')} · ${esc(row.plot_code ?? '')}</span><span>Page <span class="pno"></span></span></div></section>
<section class="page">
<h1>The plot in the dry season, a month before, and at the end · ${esc(row.plot_code ?? '')}</h1>
<div class="pair">${row.season_year != null ? `<div>${img(row.season_document_id, row)}${cap(`Apr-May ${row.season_year}`, row.season_date, row.season_ndvi)}</div>` : ''}
<div>${img(row.before_document_id, row)}${cap('A month before', row.before_date, row.before_ndvi)}</div>
<div>${img(row.after_document_id, row)}${cap('At the end', row.after_date, row.after_ndvi)}</div></div>
${row.render_bbox ? `<p class="sub">Each picture is ${esc(across(row.render_bbox) ?? '')}, north up; the plot's boundary is drawn in yellow.</p>` : ''}
<div class="foot"><span>Request ${esc(open.batch_code ?? '')} · ${esc(row.plot_code ?? '')}</span><span>Page <span class="pno"></span></span></div></section>
${seriesOf(row.plot_id).length ? `<section class="page">
<h1>Month by month · ${esc(row.plot_code ?? '')}</h1>
<p class="sub">The clearest scene within a week of day ${esc(String(new Date(`${seriesOf(row.plot_id)[0]!.target_date}T00:00:00Z`).getUTCDate()))} of each month, ${esc(across(row.render_bbox) ?? '')}, north up, boundary in yellow. NDVI: 0 bare, 1 dense green.</p>
${ndviLine(seriesOf(row.plot_id))}
<div class="grid">${seriesOf(row.plot_id).map((point) => `<div>${img(point.document_id, row)}<div class="cap"><b>${esc(MONTH(point.target_date))}</b> · ${esc(point.acquisition_date ? formatDate(point.acquisition_date) : point.mosaic ? 'clearest of the fortnight' : '--')} · NDVI ${ndvi(point.ndvi_mean)}</div></div>`).join('')}</div>
<div class="foot"><span>Request ${esc(open.batch_code ?? '')} · ${esc(row.plot_code ?? '')}</span><span>Page <span class="pno"></span></span></div></section>` : ''}`).join('')}
</body></html>`;
      const file = await htmlToPdf(html, `satellite-check-${open.batch_code ?? 'request'}.pdf`);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(file);
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The report could not be made.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <QueryState empty="No monitoring has been requested yet. Ask for one on the Request tab." query={batches}>
        <RecordTable
          columns={[
            { key: 'batch_code', label: 'Request', render: (row) => <span className="cell-stack"><strong>{row.batch_code ?? '--'}</strong><small className="muted">{KIND[row.batch_type] ?? row.batch_type}</small></span> },
            { key: 'window_from', label: 'Dates checked', render: (row) => days(row) },
            { key: 'note', label: 'Why', render: (row) => row.note || <span className="muted">--</span> },
            { key: 'requested_at', label: 'Asked', render: (row) => <span className="cell-stack">{formatDateTime(row.requested_at)}<small className="muted">{row.requested_by_name ?? ''}</small></span> },
            { key: 'plot_count', label: 'Plots', align: 'right', render: (row) => String(row.plot_count ?? 0) },
            { key: 'state', label: 'State', render: (row) => <StatusPill value={row.state} /> },
          ]}
          onRowClick={setOpen}
          rows={batches.data ?? []}
        />
      </QueryState>

      <Drawer footer={<>
                <button className="cancel" onClick={() => setOpen(null)} type="button">Close</button>
                <button className="primary" disabled={busy || !(results.data ?? []).length} onClick={() => void report()} type="button">
                  <FileDown size={14} /> {busy ? 'Making the report...' : 'Download report (PDF)'}
                </button>
              </>}
              onClose={() => setOpen(null)} open={Boolean(open)}
              subtitle={open ? `${KIND[open.batch_type] ?? open.batch_type} · ${days(open)}${open.note ? ` · ${open.note}` : ''}` : ''}
              title={open ? `Request ${open.batch_code ?? ''}` : 'Request'} wide>
        <QueryState empty="No plot in this request." query={results}>
          {(results.data ?? []).map((row) => (
            <section className="card sat-result" key={row.id}>
              <div className="section-heading">
                <h3>{row.plot_code ?? '--'} <small className="muted">{row.farmer_name ?? ''} · {hectares(row)}</small></h3>
                <StatusPill value={row.skip_reason ? 'skipped' : row.job_state} />
              </div>
              {(() => {
                const summary = landUseOf(row, evidence.data);
                return summary ? <LandUseBlock summary={summary} /> : <p style={{ margin: '0 0 8px' }}>{resultSentence(row)}</p>;
              })()}
              {row.job_state === 'completed' && <Shots ring={rings.data?.[row.plot_id]} row={row} urls={urls.data ?? {}} />}
              {seriesOf(row.plot_id).length > 0 && (
                <>
                  <h4 className="section-heading" style={{ marginTop: 12 }}>Month by month</h4>
                  <span className="sat-series-line" dangerouslySetInnerHTML={{ __html: ndviLine(seriesOf(row.plot_id)) }} />
                  <div className="sat-series">
                    {seriesOf(row.plot_id).map((point) => (
                      <figure className="sat-compare-shot" key={point.id}>
                        {point.document_id && urls.data?.[point.document_id] ? (
                          <div className="sat-compare-frame">
                            <img alt={`${row.plot_code ?? 'Plot'} ${point.target_date}`} src={urls.data[point.document_id]} />
                            <span className="sat-compare-outline" dangerouslySetInnerHTML={{ __html: outline(rings.data?.[row.plot_id], row.render_bbox) }} />
                          </div>
                        ) : <div className="sat-compare-empty">No picture</div>}
                        <figcaption><strong>{MONTH(point.target_date)}</strong> · {point.acquisition_date ? formatDate(point.acquisition_date) : point.mosaic ? 'clearest of the fortnight' : '--'}
                          {' '}· NDVI {ndvi(point.ndvi_mean)}{point.note ? <small className="muted"> · {point.note}</small> : null}</figcaption>
                      </figure>
                    ))}
                  </div>
                </>
              )}
              {row.comparison_note && <p className="muted" style={{ fontSize: '12.5px', margin: '6px 0 0' }}>{row.comparison_note}</p>}
              {row.report_document_id && urls.data?.[row.report_document_id] && (
                <p style={{ margin: '8px 0 0' }}>
                  <button className="icon-text" onClick={() => window.open(urls.data?.[row.report_document_id!], '_blank', 'noopener')} type="button">
                    <FileDown size={14} /> Report written by the satellite worker (PDF)
                  </button>
                </p>
              )}
            </section>
          ))}
          <p className="muted" style={{ fontSize: '12.5px' }}>
            Satellite observation is remote evidence. A change here is something to check against field records, never a finding on
            its own. Flagged changes are reviewed on the Flagged changes tab.
          </p>
        </QueryState>
      </Drawer>
    </>
  );
}
